import { Button, buttonVariants } from "@repo/ui/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@repo/ui/components/ui/tooltip";
import { cn } from "@repo/ui/lib/utils";
import { Link } from "@tanstack/react-router";

export interface ActionState {
  allowed: boolean;
  reason?: string;
}

interface ActionButtonBaseProps {
  state: ActionState;
  variant?: "default" | "outline" | "destructive" | "secondary" | "ghost" | "link";
  size?: "default" | "xs" | "sm" | "lg" | "icon" | "icon-xs" | "icon-sm" | "icon-lg";
  fullWidth?: boolean;
  className?: string;
  children: React.ReactNode;
}

interface ActionButtonClickProps extends ActionButtonBaseProps {
  onClick: () => void;
  to?: never;
  params?: never;
  search?: never;
}

interface ActionButtonLinkProps extends ActionButtonBaseProps {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
  onClick?: never;
}

type ActionButtonProps = ActionButtonClickProps | ActionButtonLinkProps;

const DEFAULT_REASON = "Insufficient permissions";

export function ActionButton({ state, variant, size, fullWidth, className, children, ...rest }: ActionButtonProps) {
  const cls = cn(fullWidth && "w-full", className);

  if (state.allowed) {
    if ("to" in rest && rest.to) {
      return (
        <Link
          to={rest.to}
          params={rest.params}
          search={rest.search}
          className={cn(buttonVariants({ variant, size }), cls)}
        >
          {children}
        </Link>
      );
    }
    return (
      <Button variant={variant} size={size} onClick={(rest as ActionButtonClickProps).onClick} className={cls}>
        {children}
      </Button>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger render={<span />} className={cls}>
          <Button variant={variant} size={size} disabled className={cn("pointer-events-none", cls)}>
            {children}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{state.reason ?? DEFAULT_REASON}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
